import axios from "axios";
import {
  getRefreshToken,
  getToken,
  setRefreshToken,
  setToken,
} from "../utils/storage";
import { createNotification } from "../components/Popup";
import { messages } from "../constants/messages";
import env from "../../app/config/env";

export const imageUrl = env.IMAGE_URL;
export const authUrl = env.AUTH_URL;
export const url = env.MANAGEMENT_URL;

const REFRESH_ENDPOINT = "auth/refresh";

const parseToken = (token) => {
  if (!token) return null;
  if (typeof token === "string") return token;
  if (typeof token === "object") {
    return token?.access_token || token?.token || null;
  }
  return null;
};

const isAccessTokenExpired = (accessToken) => {
  try {
    const normalizedToken = parseToken(accessToken);
    if (!normalizedToken) return true;

    const payload = JSON.parse(atob(normalizedToken.split(".")[1]));
    const expMs = (payload?.exp || 0) * 1000;
    const nowWithLeeway = Date.now() + 30 * 1000;

    return !expMs || nowWithLeeway >= expMs;
  } catch {
    return true;
  }
};

let refreshPromise = null;

const refreshAccessToken = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = parseToken(getRefreshToken());
  if (!refreshToken) {
    throw new Error("Refresh token is not available");
  }

  refreshPromise = axios
    .post(
      `${authUrl}${REFRESH_ENDPOINT}`,
      { refresh_token: refreshToken },
      { headers: { "Content-Type": "application/json" } },
    )
    .then((response) => {
      const data = response?.data?.data || response?.data || {};
      const newAccessToken = parseToken(data?.access_token);
      const newRefreshToken = parseToken(data?.refresh_token) || refreshToken;

      if (!newAccessToken) {
        throw new Error("Refresh API did not return access token");
      }

      setToken(newAccessToken);
      setRefreshToken(newRefreshToken);

      return newAccessToken;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

export const apiClient = axios.create({
  baseURL: url,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use(
  async (config) => {
    const requestUrl = config?.url || "";

    if (requestUrl.includes(REFRESH_ENDPOINT)) {
      return config;
    }

    let accessToken = parseToken(getToken());

    if (accessToken && isAccessTokenExpired(accessToken)) {
      accessToken = await refreshAccessToken();
    }

    if (accessToken) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const requestUrl = originalRequest?.url || "";
    const isUnauthorized = error?.response?.status === 401;

    if (
      !originalRequest ||
      originalRequest?._retry ||
      requestUrl.includes(REFRESH_ENDPOINT) ||
      !isUnauthorized
    ) {
      return Promise.reject(error);
    }

    try {
      originalRequest._retry = true;
      const newAccessToken = await refreshAccessToken();
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  },
);

export const protectedApi = (payload, apiUrl) => {
  return apiClient
    .post(apiUrl, payload)
    .then((response) => response?.data)
    .catch((error) => {
      const info = { type: "error", message: messages.technicalError };
      createNotification(info)();
      throw error;
    });
};

export const documentSearchApi = (payload, apiUrl) => {
  // SECURITY NOTE: This Basic Auth credential is visible in the browser bundle.
  // See SECURITY_REVIEW.md — a backend architectural change is required.
  const basicAuthToken = btoa(`${env.BASIC_AUTH_USER}:${env.BASIC_AUTH_PASS}`);
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Basic ${basicAuthToken}`,
  };

  return axios
    .post(imageUrl + apiUrl, payload, { headers })
    .then((response) => response?.data)
    .catch((error) => {
      throw error;
    });
};

export const basicApi = (payload, apiUrl) => {
  // SECURITY NOTE: This Basic Auth credential is visible in the browser bundle.
  // See SECURITY_REVIEW.md — a backend architectural change is required.
  const basicAuthToken = btoa(`${env.BASIC_AUTH_USER}:${env.BASIC_AUTH_PASS}`);
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Basic ${basicAuthToken}`,
  };

  return axios
    .post(authUrl + apiUrl, payload, { headers })
    .then((response) => response?.data)
    .catch((error) => {
      throw error;
    });
};

export const applicationOnProgress = (payload, apiUrl) => {
  return apiClient
    .post(apiUrl, payload)
    .then((response) => response?.data)
    .catch((error) => {
      throw error;
    });
};

/**
 * Alias used by legacy resource files.
 * @deprecated Use protectedApi directly.
 */
export const applicationManagement = protectedApi;

/**
 * Alias for lookup API calls.
 * @deprecated Use protectedApi directly.
 */
export const etdLookups = protectedApi;
